import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { Box, Link } from "@mui/material";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import BlogsData from "../data/blogs";
import { colors, fonts, gradients, layout } from "../theme/tokens";

const BlogPost = () => {
  const { id } = useParams();
  const blog = BlogsData.find((b) => b.link === id);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!blog) {
    return (
      <Box sx={{ overflowX: "hidden", backgroundColor: "#F5F7FA" }}>
        <Navbar />
        <Box
          sx={{
            mt: "72px",
            minHeight: "60vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            px: "24px",
            textAlign: "center",
          }}
        >
          <Box
            component="h1"
            sx={{
              fontFamily: fonts.display,
              fontWeight: 700,
              fontSize: "28px",
              color: colors.navyInk,
              mb: "12px",
            }}
          >
            Article Not Found
          </Box>
          <Box
            sx={{
              fontFamily: fonts.body,
              fontSize: "15px",
              color: colors.mutedText,
              mb: "24px",
            }}
          >
            The article you are looking for does not exist or may have been
            moved.
          </Box>
          <Link
            href="/news"
            underline="none"
            sx={{
              fontFamily: fonts.body,
              fontSize: "15px",
              fontWeight: 700,
              color: "#fff",
              background: gradients.amber,
              px: "28px",
              py: "13px",
              borderRadius: "44px",
            }}
          >
            Back to Articles
          </Link>
        </Box>
        <Footer />
      </Box>
    );
  }

  const otherBlogs = BlogsData.filter((b) => b.id !== blog.id);

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({ title: blog.name, url: window.location.href });
    } else {
      navigator.clipboard.writeText(window.location.href);
    }
  };

  return (
    <Box sx={{ overflowX: "hidden", backgroundColor: "#F5F7FA" }}>
      <Navbar />
      <Box sx={{ mt: "72px" }}>
        {/* Hero image */}
        <Box
          sx={{
            width: "100%",
            height: { xs: "260px", sm: "360px", md: "460px" },
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Box
            component="img"
            src={blog.image}
            alt={blog.name}
            sx={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              width: "100%",
              height: "55%",
              background:
                "linear-gradient(to top,rgba(14,27,42,.55),transparent)",
            }}
          />
        </Box>

        {/* Content */}
        <Box
          sx={{
            maxWidth: layout.maxWidth,
            mx: "auto",
            px: { xs: "20px", md: "28px" },
            py: { xs: "40px", md: "60px" },
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1.6fr .9fr" },
              gap: { xs: "40px", md: "56px" },
            }}
          >
            {/* Main */}
            <Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: "20px",
                  flexWrap: "wrap",
                  gap: "12px",
                }}
              >
                <Box
                  sx={{
                    fontFamily: fonts.body,
                    fontSize: "14px",
                    color: colors.subtleText,
                    fontWeight: 500,
                  }}
                >
                  {blog.date}
                </Box>
                <Box
                  onClick={handleShare}
                  sx={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    fontFamily: fonts.body,
                    fontSize: "13px",
                    fontWeight: 600,
                    color: colors.steelBlue,
                  }}
                >
                  Share
                </Box>
              </Box>
              <Box
                component="h1"
                sx={{
                  fontFamily: fonts.display,
                  fontWeight: 700,
                  fontSize: { xs: "26px", sm: "30px", md: "38px" },
                  color: colors.navyInk,
                  lineHeight: 1.18,
                  letterSpacing: "-.01em",
                  mb: "28px",
                }}
              >
                {blog.name}
              </Box>
              <Box
                sx={{ display: "flex", flexDirection: "column", gap: "20px" }}
              >
                {blog.desc.map((para, i) => (
                  <Box
                    key={i}
                    sx={{
                      fontFamily: fonts.body,
                      fontSize: { xs: "15.5px", md: "16px" },
                      color: "#42525F",
                      lineHeight: 1.8,
                    }}
                  >
                    {para}
                  </Box>
                ))}
              </Box>
              <Box sx={{ mt: "40px" }}>
                <Link
                  href="/news"
                  underline="none"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    fontFamily: fonts.body,
                    fontSize: "15px",
                    fontWeight: 700,
                    color: colors.steelBlue,
                  }}
                >
                  <Box component="span">←</Box> Back to All Articles
                </Link>
              </Box>
            </Box>

            {/* Sidebar */}
            <Box
              sx={{
                position: { md: "sticky" },
                top: "96px",
                alignSelf: "start",
              }}
            >
              <Box
                sx={{
                  fontFamily: fonts.display,
                  fontWeight: 600,
                  fontSize: "12.5px",
                  letterSpacing: ".16em",
                  color: colors.steelBlue,
                  mb: "20px",
                }}
              >
                MORE ARTICLES
              </Box>
              <Box
                sx={{ display: "flex", flexDirection: "column", gap: "16px" }}
              >
                {otherBlogs.map((item) => (
                  <Link
                    key={item.id}
                    href={`/news/${item.link}`}
                    underline="none"
                    sx={{
                      display: "flex",
                      gap: "14px",
                      p: "14px",
                      borderRadius: "16px",
                      border: `1px solid ${colors.lineSoft}`,
                      background: "#fff",
                      transition: "box-shadow .25s ease",
                      "&:hover": {
                        boxShadow: "0 8px 24px -16px rgba(14,27,42,.5)",
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src={item.image}
                      alt={item.name}
                      sx={{
                        width: "76px",
                        height: "76px",
                        borderRadius: "10px",
                        objectFit: "cover",
                        flexShrink: 0,
                      }}
                    />
                    <Box sx={{ minWidth: 0 }}>
                      <Box
                        sx={{
                          fontFamily: fonts.body,
                          fontSize: "12px",
                          color: colors.subtleText,
                          fontWeight: 500,
                          mb: "4px",
                        }}
                      >
                        {item.date}
                      </Box>
                      <Box
                        sx={{
                          fontFamily: fonts.display,
                          fontSize: "14.5px",
                          fontWeight: 600,
                          color: colors.navyInk,
                          lineHeight: 1.35,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {item.name}
                      </Box>
                    </Box>
                  </Link>
                ))}
              </Box>

              {/* CTA card */}
              <Box
                sx={{
                  mt: "28px",
                  p: "28px",
                  borderRadius: "20px",
                  background: gradients.navy,
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    right: 0,
                    width: "120px",
                    height: "120px",
                    background:
                      "radial-gradient(circle,rgba(246,166,35,.22),transparent 70%)",
                  }}
                />
                <Box
                  sx={{
                    fontFamily: fonts.display,
                    fontWeight: 700,
                    fontSize: "17px",
                    color: "#fff",
                    position: "relative",
                  }}
                >
                  Need expert guidance?
                </Box>
                <Box
                  sx={{
                    fontFamily: fonts.body,
                    fontSize: "13.5px",
                    color: "#A9BACD",
                    lineHeight: 1.6,
                    mt: "10px",
                    mb: "18px",
                    position: "relative",
                  }}
                >
                  Our team is ready to help with tailored solutions across
                  strategy, finance, advisory, risk, audit, technology, and
                  property.
                </Box>
                <Link
                  href="/contact-us"
                  underline="none"
                  sx={{
                    position: "relative",
                    display: "inline-flex",
                    alignItems: "center",
                    px: "24px",
                    py: "11px",
                    fontFamily: fonts.body,
                    fontSize: "13.5px",
                    fontWeight: 700,
                    color: "#fff",
                    background: gradients.amber,
                    borderRadius: "40px",
                  }}
                >
                  Contact Us
                </Link>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Related */}
        {otherBlogs.length > 0 && (
          <Box
            sx={{
              background: "#fff",
              borderTop: `1px solid ${colors.lineSoft}`,
              py: { xs: "50px", md: "72px" },
            }}
          >
            <Box
              sx={{
                maxWidth: layout.maxWidth,
                mx: "auto",
                px: { xs: "20px", md: "28px" },
              }}
            >
              <Box
                component="h2"
                sx={{
                  fontFamily: fonts.display,
                  fontWeight: 700,
                  fontSize: { xs: "22px", md: "28px" },
                  color: colors.navyInk,
                  mb: { xs: "28px", md: "40px" },
                }}
              >
                You might also like
              </Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "1fr 1fr",
                    md: "repeat(3,1fr)",
                  },
                  gap: "24px",
                }}
              >
                {otherBlogs.map((item) => (
                  <Link
                    key={item.id}
                    href={`/news/${item.link}`}
                    underline="none"
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      background: "#fff",
                      border: `1px solid ${colors.lineSoft}`,
                      borderRadius: "20px",
                      overflow: "hidden",
                      transition: "transform .4s cubic-bezier(.2,.7,.2,1)",
                      "&:hover": { transform: "translateY(-6px)" },
                    }}
                  >
                    <Box
                      sx={{
                        width: "100%",
                        height: { xs: "180px", md: "200px" },
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        component="img"
                        src={item.image}
                        alt={item.name}
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    </Box>
                    <Box sx={{ p: { xs: "18px", md: "22px" } }}>
                      <Box
                        sx={{
                          fontFamily: fonts.body,
                          fontSize: "12px",
                          color: colors.subtleText,
                          fontWeight: 500,
                          mb: "8px",
                        }}
                      >
                        {item.date}
                      </Box>
                      <Box
                        component="h3"
                        sx={{
                          fontFamily: fonts.display,
                          fontWeight: 600,
                          fontSize: { xs: "15px", md: "16px" },
                          color: colors.navyInk,
                          lineHeight: 1.35,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {item.name}
                      </Box>
                    </Box>
                  </Link>
                ))}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
      <Footer />
    </Box>
  );
};

export default BlogPost;
